import {useMemo} from 'react';

import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStep} from 'pipelines/PipelineTypes';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

export type DraftableStep<
  StepType extends PipelineStepType = PipelineStepType,
  StepVersion extends number = number,
> = Pick<
  PipelineStep<StepType, StepVersion>,
  'id' | 'stepType' | 'stepVersion' | 'config' | 'inputStepDatas'
>;
export type DraftStepFields = Partial<Pick<PipelineStep, 'config' | 'stepType' | 'stepVersion'>>;

export const draftStepFieldsGlobalState = makeUseGlobalClientState<DraftStepFields | null>(null);

export interface UseDraftStepOutput {
  draftSteps: {[stepId: string]: DraftableStep};
  areStepsDrafts: {[stepId: string]: boolean};
  clearDraft: (stepId: string) => void;
  updateDraftStep: (stepId: string, draftStep: Partial<DraftStepFields>) => void;
}

/**
 * Hook for managing the draft state for a list of steps.
 *
 * Supports both existing steps and steps that haven't been created yet.
 *
 * If you also want to preview the draft steps, use `useDraftStepsWithPreview` instead.
 *
 * @param steps - Array of `DraftableStep`s to manage draft state.  Draft steps only need `id`, `stepType`,
 * `stepVersion`, `config`, and `inputStepDatas` to work with this hook. Uncreated steps need to have a fake client-only ID.
 * @returns `draftSteps` - A map of step IDs to their draft state (or original state if no draft state is set yet).
 * @returns `areStepsDrafts` - A map of step IDs to whether the step is a draft.
 * @returns `clearDraft` - A function to clear the draft state for a step.
 * @returns `updateDraftStep` - A function to update the draft state for a step.
 */
export default function useDraftSteps(steps: Array<DraftableStep>): UseDraftStepOutput {
  const stepIds = steps.map((step) => step.id);
  const [draftStepFields, setDraftStepField] = draftStepFieldsGlobalState.useGlobalClientStates(stepIds);

  return {
    draftSteps: useMemo(
      () => Object.fromEntries(steps.map((step) => [step.id, {...step, ...draftStepFields[step.id]}])),
      [draftStepFields, steps]
    ),
    areStepsDrafts: useMemo(
      () => Object.fromEntries(stepIds.map((stepId) => [stepId, draftStepFields[stepId] != null])),
      [draftStepFields, stepIds]
    ),
    clearDraft: (stepId: string) => {
      draftStepFieldsGlobalState.deleteState(stepId);
    },
    updateDraftStep: (stepId: string, draftStep: Partial<DraftStepFields>) => {
      setDraftStepField(stepId, draftStep);
    },
  };
}
